import { usePrintStore, type RemotePrinterEntry } from '@renderer/stores/printStore'

/**
 * Fetch the remote-printer registry and mirror it into the print store.
 *
 * Two callers with very different stakes share this:
 *  - Device Setup, to populate the printer picker, and
 *  - `PrintUtils.sendToPrinter`, immediately before a remote job.
 *
 * The second one is why this exists as a util: the registry row a booth
 * selected can go stale between sessions — quick tunnels get a NEW URL every
 * time the host app restarts, and the old row is pruned after its heartbeat
 * TTL. A client that only refreshes the list while Device Setup is open would
 * keep firing jobs at a tunnel that no longer exists (cikstik iPad, 2026-08-26).
 * Re-resolving at print time picks up the fresh tunnel and availability with no
 * operator involvement.
 */
export async function refreshRemotePrinterRegistry(
  timeoutMs = 6000
): Promise<boolean> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(`${process.env.VITE_API_URL}/api/remote-printer`, {
      credentials: 'include',
      cache: 'no-cache',
      signal: controller.signal
    })
    if (!response.ok) return false
    const data = await response.json()
    const printers = Array.isArray(data?.printers) ? data.printers : []
    usePrintStore.getState().setRemotePrinters(
      printers
        .filter((p: { tunnelUrl?: string }) => p?.tunnelUrl)
        .map(
          (p: {
            _id: unknown
            tunnelUrl: string
            printerName?: string
            machineName?: string
            projectName?: string
            printSettings?: unknown
            printers?: unknown
            isAvailable?: boolean
          }) => ({
            machineIdentifier: String(p._id),
            tunnelUrl: p.tunnelUrl,
            printerName: p.printerName || 'DS-RX1',
            machineName: p.machineName || undefined,
            projectName: p.projectName || undefined,
            printSettings: p.printSettings,
            // The API always sends a list (synthesising one for booths too old
            // to publish theirs), but guard anyway so a stale/failed response
            // can't put a non-array into the picker.
            printers: Array.isArray(p.printers)
              ? (p.printers as RemotePrinterEntry[])
              : undefined,
            isAvailable: p.isAvailable ?? true
          })
        )
    )
    return true
  } catch (error) {
    // Offline or slow API: callers keep whatever registry snapshot they have.
    console.warn('[REMOTE-PRINT] Registry refresh failed:', error)
    return false
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Human label for a registry machine. Registrations from booths on older
 * shells carry no machineName (the preload didn't expose the hostname), which
 * used to render every row as an identical "Booth PC" — four of those in the
 * picker is how a karcis ends up printing at another venue. The tunnel
 * subdomain fragment is stable for the life of that registration and unique
 * per machine, so it disambiguates without any host-side change.
 */
export function remoteMachineDisplayLabel(machine: {
  machineName?: string
  projectName?: string
  tunnelUrl?: string
}): string {
  const base = machine.machineName || machine.projectName || 'Booth PC'
  if (machine.machineName) return base
  const fragment = tunnelFragment(machine.tunnelUrl)
  return fragment ? `${base} · ${fragment}` : base
}

/** First two words of the trycloudflare subdomain — short, stable, unique per registration. */
export function tunnelFragment(tunnelUrl?: string): string {
  if (!tunnelUrl) return ''
  try {
    return new URL(tunnelUrl).hostname.split('.')[0].split('-').slice(0, 2).join('-')
  } catch {
    return ''
  }
}
